import {
  isWebScalingEnabled,
  subscribeWebScalingConfig,
} from '@/core/services/webScalingConfigService';
import {
  computeDesktopFitScale,
  readViewportMetrics,
  subscribeViewportChanges,
  VIEWPORT_ZOOM_EPSILON,
  type ViewportMetrics,
} from '@/core/utils/viewportMetrics';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, useWindowDimensions } from 'react-native';

export interface WebViewportState extends ViewportMetrics {
  /** Visible width when scaling enabled; layout width when disabled. */
  breakpointWidth: number;
  /**
   * Zoom-corrected height to use instead of window.innerHeight / useWindowDimensions().height.
   *
   * Raw window.innerHeight is reduced by the browser zoom (e.g. 614px at 125% on a 768px screen).
   * When CSS zoom is applied to <html> (e.g. zoom: 0.8), any container that sets its height to
   * the raw pixel value will render at only 80% of the viewport, leaving a white gap.
   * breakpointHeight = layoutHeight / cssZoom corrects for this — use it wherever you would
   * otherwise write `useWindowDimensions().height` in a web layout context.
   */
  breakpointHeight: number;
}

const WebViewportContext = createContext<WebViewportState | null>(null);

function toViewportState(metrics: ViewportMetrics): WebViewportState {
  if (!isWebScalingEnabled()) {
    return { ...metrics, breakpointWidth: metrics.layoutWidth, breakpointHeight: metrics.layoutHeight };
  }

  const cssZoom = computeDesktopFitScale(metrics);
  const hasScale = cssZoom < 1 - VIEWPORT_ZOOM_EPSILON;

  // When CSS zoom is active (< 1), the apparent viewport width the browser
  // presents to the user is wider than window.innerWidth.
  // e.g. at 125% zoom: innerWidth=819, cssZoom=0.8 → apparent=819/0.8=1024px
  // Layout components should size against this apparent width so their
  // computed pixel values match what actually renders on screen.
  const breakpointWidth = hasScale
    ? metrics.layoutWidth / cssZoom
    : metrics.layoutWidth;

  // Same correction for height: raw window.innerHeight is already shrunk by the
  // browser zoom. Applying CSS zoom on top shrinks it further, leaving a white gap.
  const breakpointHeight = hasScale
    ? metrics.layoutHeight / cssZoom
    : metrics.layoutHeight;

  return { ...metrics, breakpointWidth, breakpointHeight };
}

function readNativeViewport(width: number, height: number): WebViewportState {
  const metrics: ViewportMetrics = {
    visibleWidth: width,
    layoutWidth: width,
    visibleHeight: height,
    layoutHeight: height,
    browserZoom: 1,
  };
  return { ...toViewportState(metrics), breakpointHeight: height };
}

/** Single subscription for the whole app (web). */
function WebViewportProviderInner({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const { width: rnWidth, height: rnHeight } = useWindowDimensions();
  const [configRevision, setConfigRevision] = useState(0);
  const [state, setState] = useState<WebViewportState>(() =>
    Platform.OS === 'web'
      ? toViewportState(readViewportMetrics())
      : readNativeViewport(rnWidth, rnHeight),
  );

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    return subscribeWebScalingConfig(() => {
      setConfigRevision((revision) => revision + 1);
    });
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      setState(readNativeViewport(rnWidth, rnHeight));
      return;
    }

    const sync = () => {
      setState(toViewportState(readViewportMetrics()));
    };

    sync();
    return subscribeViewportChanges(sync);
  }, [rnWidth, rnHeight, configRevision]);

  const value = useMemo(() => state, [state]);

  return (
    <WebViewportContext.Provider value={value}>
      {children}
    </WebViewportContext.Provider>
  );
}

export function WebViewportProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return <WebViewportProviderInner>{children}</WebViewportProviderInner>;
}

/**
 * Reactive viewport: visible width/height, browser zoom, fit-scale.
 * Requires WebViewportProvider in app root (_layout.tsx).
 */
export function useWebViewport(): WebViewportState {
  const context = useContext(WebViewportContext);
  if (!context) {
    throw new Error(
      'useWebViewport must be used within WebViewportProvider (see app/_layout.tsx)',
    );
  }
  return context;
}
